import { useCallback } from "react";

import { saveAs } from "file-saver";
import toast from "react-hot-toast";

import { useDownloadWorkAttachmentsMutation } from "@/generated/graphql";

interface DownloadWorkAttachmentsPayload {
  answerBlockId: string;
  blockAttachmentIds: Array<string>;
}

/**
 * Hook to download work file attachments (single or multiple as ZIP).
 */
export function useDownloadWorkAttachments() {
  const [downloadWorkAttachment] = useDownloadWorkAttachmentsMutation();

  return useCallback(
    async (payload: DownloadWorkAttachmentsPayload) => {
      const { answerBlockId, blockAttachmentIds } = payload;

      if (blockAttachmentIds.length === 0) {
        toast.error("No attachments to download");
        return;
      }

      if (!answerBlockId) {
        toast.error("Answer block ID is required to download attachments");
        return;
      }

      async function download() {
        // Call the GraphQL mutation to get the download URL for archived zip
        const { data } = await downloadWorkAttachment({
          variables: {
            answerBlockId,
            blockAttachmentIds,
          },
        });

        if (!data?.downloadWorkAttachments) {
          throw new Error("Failed to get download URL");
        }

        if (blockAttachmentIds.length === 1) {
          // Single file download
          const filename = `attachment-${blockAttachmentIds[0]}`;
          const response = await fetch(data.downloadWorkAttachments);
          const blob = await response.blob();
          saveAs(blob, filename);
          return filename;
        }

        const downloadUrl = data.downloadWorkAttachments;

        const filename = `attachments-${answerBlockId}.zip`;
        const response = await fetch(downloadUrl);
        const blob = await response.blob();
        saveAs(blob, filename);

        return filename;
      }

      const promise = download();

      // Show toast notifications
      const count = blockAttachmentIds.length;

      toast.promise(promise, {
        loading: `Downloading ${count} ${count === 1 ? "file" : "files"}...`,
        success: `Downloaded ${count} ${count === 1 ? "file" : "files"}`,
        error: "Download failed. Please try again.",
      });

      return promise;
    },
    [downloadWorkAttachment]
  );
}
